import { Link, Navigate, useNavigate } from "react-router-dom";
import { useState } from "react";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { GlofoxBrandLogo } from "@/components/portal/GlofoxBrandLogo";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  isAdminLoginFormValid,
  resolveAdminLoginError,
  toAdminLoginRequest,
  type AdminLoginError,
} from "@/lib/api/admin-login-form";
import { loginAdmin } from "@/lib/api/admin-login-user";
import {
  getAdminAuthSession,
  setAdminAuthSession,
} from "@/lib/auth/admin-session";

import "@/styles/admin-panel.css";

export default function AdminLoginPage() {
  usePageTitle("Admin log in");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<AdminLoginError | null>(null);
  const [loading, setLoading] = useState(false);

  if (getAdminAuthSession()) {
    return <Navigate to="/admin/control-panel" replace />;
  }

  const canSubmit = isAdminLoginFormValid({ email, password });

  async function handleLogin() {
    if (!canSubmit) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await loginAdmin(toAdminLoginRequest({ email, password }));
      setAdminAuthSession({
        accessToken: response.access_token,
        tokenType: response.token_type,
        expiresIn: response.expires_in,
        user: response.user,
      });
      navigate("/admin/control-panel");
    } catch (err: unknown) {
      setError(resolveAdminLoginError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-login" data-testid="admin-login-shell">
      <div className="admin-login-panel">
        <div className="admin-login-brand">
          <div className="admin-login-brand-row">
            <AbcFitnessBrandLogo
              to=""
              testId="admin-login-abc-logo"
              theme="dark"
            />
            <span className="admin-login-sep" aria-hidden>
              /
            </span>
            <GlofoxBrandLogo to="" testId="admin-login-glofox-logo" />
          </div>
          <div className="admin-login-product">API Admin Panel</div>
        </div>

        <div className="auth-card admin-login-card" data-testid="admin-login-card">
          <div className="auth-hdr admin-login-hdr">
            <p className="auth-eye">Staff access</p>
            <h2 data-testid="admin-login-title">Admin sign in</h2>
            <p>Use your internal admin credentials.</p>
          </div>
          <div className="auth-body">
            {error ? (
              <div
                className="error-msg"
                data-testid="admin-login-error"
                role="alert"
              >
                <i className="ti ti-alert-circle" aria-hidden="true" />
                <span>{error.message}</span>
              </div>
            ) : null}

            <div className="field">
              <label htmlFor="admin-login-email">Email address</label>
              <input
                id="admin-login-email"
                type="email"
                data-testid="admin-login-email"
                placeholder="you@company.com"
                autoComplete="username"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              />
            </div>

            <div className="field">
              <label htmlFor="admin-login-password">Password</label>
              <div className="admin-login-pw">
                <input
                  id="admin-login-password"
                  type={showPw ? "text" : "password"}
                  data-testid="admin-login-password"
                  placeholder="Your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
                <button
                  className="show-pw-btn"
                  data-testid="admin-login-toggle-password"
                  onClick={() => setShowPw((v) => !v)}
                  type="button"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  aria-pressed={showPw}
                >
                  <i
                    className={`ti ${showPw ? "ti-eye-off" : "ti-eye"}`}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>

            <p style={{ margin: "0 0 1rem", fontSize: 13, textAlign: "right" }}>
              <Link
                to="/admin/forgot-password"
                data-testid="admin-login-forgot-password"
              >
                Forgot password?
              </Link>
            </p>

            <button
              type="button"
              className="submit-btn admin-login-submit"
              data-testid="admin-login-submit"
              onClick={handleLogin}
              disabled={loading || !canSubmit}
            >
              {loading ? (
                <>
                  <i className="ti ti-loader-2 admin-login-spinner" aria-hidden="true" />{" "}
                  Signing in…
                </>
              ) : (
                <>
                  <i className="ti ti-shield-lock" aria-hidden="true" /> Sign in
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
